import React, { useState, useEffect } from "react";
import {ListItem, Typography} from "@mui/material";
import Div from "@jumbo/shared/Div";
import List from "@mui/material/List";
import styled from "@emotion/styled";
import {useTranslation} from "react-i18next";
import {Alert, Stack, Box,Button} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getUserTimeZone } from "../../../services/config";



const StyledCircle = styled('div')(({theme, color}) => ({
    backgroundColor: color,
    width: '10px',
    height: '10px',
    borderRadius: '50%',
}));

const LegendItem = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
}));

const LegendIcon = styled("div")(
    ({ theme, borderColor, fillColor, dotColor }) => ({
        width: 22,
        height: 22,
        borderRadius: 6,
        border: `2px solid ${borderColor}`,
        backgroundColor: fillColor || "transparent",
        display: "grid",
        placeItems: "center",
        boxSizing: "border-box",
        ...(dotColor
            ? {
                "&::after": {
                    content: '""',
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: dotColor,
                    display: "block",
                },
            }
            : {}),
    })
);


const Header = ({title, subheader}) => {

    const {t} = useTranslation();

    const [showBookHint, setShowBookHint] = useState(false);
    const [showReportHint, setShowReportHint] = useState(false);

    useEffect(() => {
        const bookHintHidden = localStorage.getItem("calendarBookHintHidden");
        const reportHintHidden = localStorage.getItem("calendarReportHintHidden");

        setShowBookHint(bookHintHidden !== "true");
        setShowReportHint(reportHintHidden !== "true");
    }, []);

    const handleHideBookHint = () => {
        setShowBookHint(false);
        localStorage.setItem("calendarBookHintHidden", "true");
    };

    const handleHideReportHint = () => {
        setShowReportHint(false);
        localStorage.setItem("calendarReportHintHidden", "true");
    };



    return (
        <Div
            sx={{
                display: 'flex',
                flexDirection: 'column',
                mb: 4
            }}
        >
            <Typography variant={"h2"} sx={{ fontFamily: "'Nunito', sans-serif" }}>
                {title}
            </Typography>
            <Typography variant={"body1"} sx={{ fontFamily: "'Nunito', sans-serif" }} mb={2} color={"text.secondary"}>{subheader}</Typography>
            {/*<Stack sx={{width: '100%'}} spacing={2}>
                <Alert severity="info">{t('calendar.alertTxt')}</Alert>
            </Stack>*/}

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                    mt:2
                }}
            >
                {/* Globe icon (same look as screenshot) */}
                <Box
                    sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        //background: "linear-gradient(135deg, #3B82F6, #22D3EE)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 18,   // 👈 increased
                        lineHeight: 1,
                    }}
                >
                    🌍
                </Box>


                {/* Same font + weight */}
                <Typography
                    sx={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: "#666",
                        fontFamily: "'Nunito', sans-serif",
                    }}
                >
                    {t("parent.title.caleanderTimezone")}: {getUserTimeZone()}
                </Typography>
            </Box>



            <Box sx={{ mt: 2 }}>
                {showBookHint && (
                    <Box
                        sx={{
                            border: "2px solid #FACC15",
                            borderRadius: 2,
                            backgroundColor: "#FFF7E6",
                            px: 2,
                            py: 1.25,
                            mb: 1.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {/* Square Info Icon */}
                            <Box
                                sx={{
                                    width: 22,
                                    height: 22,
                                    borderRadius: 1,
                                    backgroundColor: "#2563EB",
                                    color: "#fff",
                                    fontSize: 14,
                                    fontWeight: 700,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    lineHeight: 1,
                                }}
                            >
                                i
                            </Box>


                            <Typography
                                variant="body2"
                                sx={{
                                    fontFamily: "'Nunito', sans-serif",
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color:"#002465"
                                }}
                            >
                                {t("parent.title.calendar-sub1")}
                            </Typography>
                        </Box>

                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleHideBookHint}
                            sx={{
                                backgroundColor: "#1D6ADE",
                                color: "#ffffff",
                                border: "none",
                                px: "16px",          // padding left/right
                                py: "6px",           // padding top/bottom
                                borderRadius: "6px",
                                fontWeight: 700,
                                fontSize: "12px",
                                cursor: "pointer",
                                fontFamily: "'Nunito', sans-serif",
                                transition: "all 0.3s",
                                whiteSpace: "nowrap",
                                textTransform: "none", // prevents uppercase in MUI Button

                                "&:hover": {
                                    backgroundColor: "#1557b8", // optional hover (recommended)
                                },

                                "&:active": {
                                    backgroundColor: "#134ea6",
                                },

                                "&.Mui-disabled": {
                                    backgroundColor: "#9bbcf2",
                                    color: "#ffffff",
                                    cursor: "not-allowed",
                                },
                            }}
                        >
                            {t("parent.title.calendar-btn")}
                        </Button>

                    </Box>
                )}

                {showReportHint && (
                    <Box
                        sx={{
                            border: "2px solid #FACC15",
                            borderRadius: 2,
                            backgroundColor: "#FFF7E6",
                            px: 2,
                            py: 1.25,
                            mb: 1.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {/* Square Info Icon */}
                            <Box
                                sx={{
                                    width: 22,
                                    height: 22,
                                    borderRadius: 1,
                                    backgroundColor: "#2563EB",
                                    color: "#fff",
                                    fontSize: 14,
                                    fontWeight: 700,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    lineHeight: 1,
                                }}
                            >
                                i
                            </Box>


                            <Typography variant="body2" sx={{
                                fontFamily: "'Nunito', sans-serif",
                                fontSize: 13,
                                fontWeight: 600,
                                color:"#002465"
                            }}>
                                {t("parent.title.calendar-sub2")}
                            </Typography>
                        </Box>

                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleHideReportHint}
                            sx={{
                                backgroundColor: "#1D6ADE",
                                color: "#ffffff",
                                border: "none",
                                px: "16px",          // padding left/right
                                py: "6px",           // padding top/bottom
                                borderRadius: "6px",
                                fontWeight: 700,
                                fontSize: "12px",
                                cursor: "pointer",
                                fontFamily: "'Nunito', sans-serif",
                                transition: "all 0.3s",
                                whiteSpace: "nowrap",
                                textTransform: "none", // prevents uppercase in MUI Button

                                "&:hover": {
                                    backgroundColor: "#1557b8", // optional hover (recommended)
                                },

                                "&:active": {
                                    backgroundColor: "#134ea6",
                                },

                                "&.Mui-disabled": {
                                    backgroundColor: "#9bbcf2",
                                    color: "#ffffff",
                                    cursor: "not-allowed",
                                },
                            }}
                        >
                            {t("parent.title.calendar-btn")}
                        </Button>

                    </Box>
                )}
            </Box>




            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 3,
                    mt: 2,
                }}
            >
                {/* Booked (outlined blue) */}
                <LegendItem>
                    <LegendIcon borderColor="#2196f3" fillColor="#bbdefb" />
                    <Typography variant="body2" sx={{fontFamily: "'Nunito', sans-serif",
                        fontStyle: "normal",
                        fontWeight: 400,color:"#666"}}>{t("common.readyBookingNew")}</Typography>
                </LegendItem>

                {/* Completed (filled blue) */}
                <LegendItem>
                    <LegendIcon borderColor="#1557b8" fillColor="#1d6ade" />
                    <Typography variant="body2" sx={{fontFamily: "'Nunito', sans-serif",
                        fontStyle: "normal",
                        fontWeight: 400,color:"#666"}}>{t("common.bookingCompletedNew")}</Typography>
                </LegendItem>

                {/* Cancelled (outlined red) */}
                <LegendItem>
                    <LegendIcon borderColor="#f44336" fillColor="#ffcdd2" />
                    <Typography variant="body2" sx={{fontFamily: "'Nunito', sans-serif",
                        fontStyle: "normal",
                        fontWeight: 400,color:"#666"}}>{t("common.courseCancelNew")}</Typography>
                </LegendItem>

                {/* Child Absent (outlined blue with red dot) */}
                <LegendItem>
                    <LegendIcon borderColor="#1D6ADE" dotColor="#D22B2B" fillColor="#e1f5fe" />
                    <Typography variant="body2" sx={{fontFamily: "'Nunito', sans-serif",
                        fontStyle: "normal",
                        fontWeight: 400,color:"#666"}}>{t("common.notAttendedNew")}</Typography>
                </LegendItem>
            </Box>

            {/*<List
                disablePadding
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    margin: theme => theme.spacing(0, -2),
                    mb: 2,
                    mt: 2
                }}
            >


                <ListItem
                    sx={{
                        width: {xs: '100%', sm: '50%', xl: '20%'}, mt: 1
                    }}
                >
                    <Div sx={{
                        backgroundColor: "#59B2FF",
                        minWidth: '90%',
                        minHeight: 50,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Typography variant="body1" color="white">
                            {t('common.readyBooking')}
                        </Typography>
                    </Div>
                </ListItem>

                <ListItem
                    sx={{
                        width: {xs: '100%', sm: '50%', xl: '20%'}, mt: 1
                    }}
                >
                    <Div sx={{
                        backgroundColor: "#1D6ADE",
                        minWidth: '90%',
                        minHeight: 50,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}><Typography variant="body1" color="white">
                        {t('common.bookingCompleted')}
                    </Typography></Div>

                </ListItem>

                <ListItem
                    sx={{
                        width: {xs: '100%', sm: '50%', xl: '20%'}, mt: 1
                    }}
                >
                    <Div sx={{
                        backgroundColor: "#D22B2B",
                        minWidth: '90%',
                        minHeight: 50,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Typography variant="body1" color="white">
                            {t("common.courseCancel")}</Typography>
                    </Div>
                </ListItem>

                <ListItem
                    sx={{
                        width: {xs: '100%', sm: '50%', xl: '20%'}, mt: 1
                    }}
                >
                    <Div sx={{
                        backgroundColor: "#dedede",
                        minWidth: '90%',
                        minHeight: 50,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>                         <StyledCircle color="#D22B2B"/>

                        <Typography sx={{ ml : 0.5}} variant="body1" color="white">
                            {t('common.notAttended')}
                        </Typography>

                    </Div>
                </ListItem>


            </List>*/}
        </Div>
    );
};

export default Header;
